import { t } from "../../i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  Clock3,
  Pencil,
  Play,
  Plus,
  Power,
  Trash2,
} from "lucide-react";
import { modelSupportsVision } from "@lumen/shared";
import type {
  AgentDefinition,
  Attachment,
  Automation,
  AutomationEventTrigger,
  AutomationRun,
  Integration,
  Model,
  ThinkingLevel,
  User,
} from "@lumen/shared";
import { api } from "../../api";
import { completePersonalizationRequestFromLocation } from "../../personalization";
import {
  Composer,
  type ComposerOptions,
} from "../../components/composer/Composer";
import { ModelSearchPicker } from "../../components/composer/ModelSearchPicker";
import { AutomationRunIcon } from "../../components/automations/AutomationRunIcon";
import { AutomationEmptyState } from "../../components/automations/AutomationEmptyState";

const schedules = [
  { label: "Every hour", cron: "0 * * * *" },
  { label: "Every day", cron: "0 9 * * *" },
  { label: "Weekdays", cron: "0 9 * * 1-5" },
  { label: "Every week", cron: "0 9 * * 1" },
  { label: "Custom", cron: "" },
];

function toDateTimeLocal(value: string) {
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
}

export function AutomationsPage({
  user,
  organizationOnly = false,
}: {
  user: User;
  organizationOnly?: boolean;
}) {
  const [items, setItems] = useState<Automation[]>([]);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Automation | null>(null);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [models, setModels] = useState<Model[]>([]);
  const [agents, setAgents] = useState<AgentDefinition[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [eventTrigger, setEventTrigger] =
    useState<AutomationEventTrigger | null>(null);
  const [eventTriggerEnabled, setEventTriggerEnabled] = useState(false);
  const [eventIntegrationId, setEventIntegrationId] = useState("");
  const [eventType, setEventType] =
    useState<AutomationEventTrigger["eventType"]>("message_received");
  const [senderFilter, setSenderFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [bodyFilter, setBodyFilter] = useState("");
  const [conversationMode, setConversationMode] = useState<"thread" | "event">(
    "thread",
  );
  const [responseMode, setResponseMode] = useState<
    "none" | "full_response" | "tool"
  >("none");
  const [name, setName] = useState("");
  const [modelId, setModelId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel | "">("");
  const [scheduleType, setScheduleType] = useState<"cron" | "once">("cron");
  const [preset, setPreset] = useState("0 9 * * *");
  const [cron, setCron] = useState("0 9 * * *");
  const [runAt, setRunAt] = useState("");
  const [visibility, setVisibility] = useState<"personal" | "organization">(
    organizationOnly ? "organization" : "personal",
  );
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const visibleModels = useMemo(
    () =>
      visibility === "organization"
        ? models.filter((m) => m.scope === "platform")
        : models,
    [models, visibility],
  );
  const selectedModel = visibleModels.find((model) => model.id === modelId);
  const formTimezone = editing?.timezone ?? timezone;
  const load = useCallback(
    () =>
      Promise.all([
        api<Automation[]>("/automations"),
        api<AutomationRun[]>("/automation-runs"),
      ]).then(([a, r]) => {
        setItems(
          organizationOnly
            ? a.filter((item) => item.visibility === "organization")
            : a,
        );
        setRuns(r);
      }),
    [organizationOnly],
  );
  useEffect(() => {
    void load();
    void Promise.all([
      api<Model[]>("/models"),
      api<AgentDefinition[]>("/agents/available"),
    ]).then(([m, a]) => {
      const enabled = m.filter((x) => x.enabled);
      setModels(enabled);
      setAgents(a);
      setModelId(enabled[0]?.id ?? "");
      setAgentId(a.find((x) => x.slug === "chat")?.id ?? a[0]?.id ?? "");
    });
    if (user.organizationPermissions.includes("integrations:use"))
      void api<Integration[]>("/integrations").then(setIntegrations);
  }, [load, user.organizationPermissions]);
  useEffect(() => {
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [load]);
  const recentByAutomation = useMemo(
    () => new Map(runs.map((run) => [run.automationId, run])),
    [runs],
  );
  const openNew = () => {
    setEditing(null);
    setName("");
    setModelId(models[0]?.id ?? "");
    setAgentId(
      agents.find((x) => x.slug === "chat")?.id ?? agents[0]?.id ?? "",
    );
    setThinkingLevel("");
    setScheduleType("cron");
    setPreset("0 9 * * *");
    setCron("0 9 * * *");
    setRunAt("");
    setVisibility(organizationOnly ? "organization" : "personal");
    setEventTrigger(null);
    setEventTriggerEnabled(false);
    setEventIntegrationId("");
    setEventType("message_received");
    setSenderFilter("");
    setSubjectFilter("");
    setBodyFilter("");
    setConversationMode("thread");
    setResponseMode("none");
    setError("");
    setCreating(true);
  };
  const openEdit = (item: Automation) => {
    setCreating(false);
    setEditing(item);
    setName(item.name);
    setModelId(item.modelId ?? "");
    setAgentId(item.agentId ?? item.messageInput.agentId ?? "");
    setThinkingLevel(item.messageInput.thinkingLevel ?? "");
    setScheduleType(item.scheduleType);
    setCron(item.cronExpression ?? "0 9 * * *");
    setPreset(
      item.scheduleType === "cron" &&
        schedules.some((schedule) => schedule.cron === item.cronExpression)
        ? (item.cronExpression ?? "")
        : item.scheduleType === "once"
          ? "0 9 * * *"
          : "",
    );
    setRunAt(item.runAt ? toDateTimeLocal(item.runAt) : "");
    setVisibility(item.visibility);
    setError("");
    setEventTrigger(null);
    setEventTriggerEnabled(false);
    void api<AutomationEventTrigger | null>(
      `/automations/${item.id}/event-trigger`,
    ).then((trigger) => {
      setEventTrigger(trigger);
      setEventTriggerEnabled(Boolean(trigger?.enabled));
      setEventIntegrationId(trigger?.integrationId ?? "");
      setEventType(trigger?.eventType ?? "message_received");
      setSenderFilter(trigger?.filters.senderContains ?? "");
      setSubjectFilter(trigger?.filters.subjectContains ?? "");
      setBodyFilter(trigger?.filters.bodyContains ?? "");
      setConversationMode(trigger?.conversationMode ?? "thread");
      setResponseMode(trigger?.responseMode ?? "none");
    });
  };
  const closeForm = () => {
    setCreating(false);
    setEditing(null);
    setError("");
  };
  const save = async (
    content: string,
    attached: Attachment[],
    options: ComposerOptions,
  ) => {
    const current = editing;
    setBusy(true);
    setError("");
    try {
      const saved = await api<Automation>(
        current ? `/automations/${current.id}` : "/automations",
        {
          method: current ? "PATCH" : "POST",
          body: JSON.stringify({
            name,
            prompt: content,
            modelId,
            agentId: options.agentId,
            visibility: organizationOnly ? "organization" : visibility,
            scheduleType,
            cronExpression: scheduleType === "cron" ? cron : null,
            runAt:
              scheduleType === "once" ? new Date(runAt).toISOString() : null,
            timezone: formTimezone,
            messageInput: {
              ...options,
              attachmentIds:
                attached.length > 0
                  ? attached.map((file) => file.id)
                  : (current?.messageInput.attachmentIds ?? []),
            },
          }),
        },
      );
      if (!current)
        await completePersonalizationRequestFromLocation({
          resourceType: "automation",
          resourceId: saved.id,
        }).catch(() => undefined);
      if (eventTriggerEnabled && eventIntegrationId) {
        await api(`/automations/${saved.id}/event-trigger`, {
          method: "PUT",
          body: JSON.stringify({
            integrationId: eventIntegrationId,
            eventType,
            filters: {
              ...(senderFilter ? { senderContains: senderFilter } : {}),
              ...(subjectFilter ? { subjectContains: subjectFilter } : {}),
              ...(bodyFilter ? { bodyContains: bodyFilter } : {}),
            },
            conversationMode,
            responseMode,
            enabled: true,
          }),
        });
      } else if (current && eventTrigger) {
        await api(`/automations/${saved.id}/event-trigger`, {
          method: "DELETE",
        });
      }
      closeForm();
      setName("");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save automation",
      );
      throw reason;
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (item: Automation) => {
    setTogglingId(item.id);
    setError("");
    try {
      await api(`/automations/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !item.enabled }),
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not update automation",
      );
    } finally {
      setTogglingId(null);
    }
  };
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-stone-50/50 p-4 md:p-8 dark:bg-transparent">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-[.18em] text-lumen-600 uppercase">
              {t("Scheduled prompts")}
            </p>
            <h1 className="mt-1 text-3xl font-bold">{t("Automations")}</h1>
            <p className="mt-2 text-sm text-stone-500">
              {t("The same message composer as chat, scheduled for later.")}
            </p>
          </div>
          <button
            className="button-primary"
            onClick={creating || editing ? closeForm : openNew}
          >
            <Plus size={17} />{" "}
            {creating || editing ? t("Close") : t("New automation")}
          </button>
        </div>
        {error && !creating && !editing && (
          <p className="mt-4 text-sm text-red-600">{error}</p>
        )}
        {(creating || editing) && (
          <section className="panel mt-6 p-5 md:p-7">
            <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
              <div className="space-y-4">
                <label className="block">
                  <span className="label">{t("Name")}</span>
                  <input
                    className="field"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("Morning briefing")}
                  />
                </label>
                <label className="block">
                  <span className="label">{t("Model")}</span>
                  <ModelSearchPicker
                    models={visibleModels}
                    value={modelId}
                    onChange={setModelId}
                  />
                </label>
                <div>
                  <span className="label">{t("Message")}</span>
                  <Composer
                    key={editing?.id ?? "new-automation"}
                    mode="edit"
                    onCancel={closeForm}
                    onSend={save}
                    disabled={
                      busy ||
                      !name ||
                      !modelId ||
                      (scheduleType === "once" && !runAt) ||
                      (eventTriggerEnabled && !eventIntegrationId)
                    }
                    agents={agents}
                    agentId={agentId}
                    onAgentChange={setAgentId}
                    supportsVision={
                      selectedModel ? modelSupportsVision(selectedModel) : false
                    }
                    supportsTools={Boolean(
                      selectedModel?.capabilities.tools &&
                      selectedModel.toolsEnabled,
                    )}
                    thinkingLevels={selectedModel?.thinkingLevels}
                    supportsThinking={selectedModel?.supportedParameters.some(
                      (parameter) =>
                        parameter === "reasoning" ||
                        parameter === "reasoning_effort",
                    )}
                    supportedParameters={
                      selectedModel?.supportedParameters ?? []
                    }
                    thinkingLevel={thinkingLevel}
                    onThinkingLevelChange={setThinkingLevel}
                    initialValue={editing?.prompt ?? ""}
                    initialOptions={editing?.messageInput}
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
              </div>
              <div className="rounded-2xl bg-[#f4f7f5] p-5 dark:bg-white/4">
                <div className="mb-4 flex rounded-xl bg-white p-1 dark:bg-black/20">
                  <button
                    className={`flex-1 rounded-lg px-3 py-2 text-sm ${scheduleType === "cron" ? "bg-lumen-600 text-white" : ""}`}
                    onClick={() => setScheduleType("cron")}
                  >
                    {t("Recurring")}
                  </button>
                  <button
                    className={`flex-1 rounded-lg px-3 py-2 text-sm ${scheduleType === "once" ? "bg-lumen-600 text-white" : ""}`}
                    onClick={() => setScheduleType("once")}
                  >
                    {t("One time")}
                  </button>
                </div>
                {scheduleType === "cron" ? (
                  <>
                    <span className="label">{t("How often?")}</span>
                    <div className="grid grid-cols-2 gap-2">
                      {schedules.map((s) => (
                        <button
                          key={s.label}
                          className={`rounded-xl border p-2 text-sm ${preset === s.cron ? "border-lumen-500 bg-lumen-50 text-lumen-800 dark:bg-lumen-900/30 dark:text-lumen-200" : "border-stone-200 dark:border-white/10"}`}
                          onClick={() => {
                            setPreset(s.cron);
                            if (s.cron) setCron(s.cron);
                          }}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    {preset === "" && (
                      <label className="mt-3 block">
                        <span className="label">{t("Cron expression")}</span>
                        <input
                          className="field font-mono"
                          value={cron}
                          onChange={(e) => setCron(e.target.value)}
                        />
                        <span className="mt-1 block text-xs text-stone-400">
                          {t("minute · hour · day · month · weekday")}
                        </span>
                      </label>
                    )}
                  </>
                ) : (
                  <label>
                    <span className="label">{t("Run on")}</span>
                    <input
                      type="datetime-local"
                      className="field"
                      value={runAt}
                      onChange={(e) => setRunAt(e.target.value)}
                    />
                  </label>
                )}
                <p className="mt-4 text-xs text-stone-400">
                  <Clock3 className="mr-1 inline" size={13} />
                  {t("Times use {{timezone}}", { timezone: formTimezone })}
                </p>
                {organizationOnly ? (
                  <p className="mt-4 text-sm text-stone-500">
                    {t("Organization")}
                  </p>
                ) : (
                  user.organizationPermissions.includes(
                    "automations:manage",
                  ) && (
                    <label className="mt-4 block">
                      <span className="label">{t("Who can see it?")}</span>
                      <select
                        className="field"
                        value={visibility}
                        onChange={(e) => {
                          const next = e.target.value as typeof visibility;
                          setVisibility(next);
                          if (
                            next === "organization" &&
                            modelId &&
                            models.find((m) => m.id === modelId)?.scope ===
                              "personal"
                          )
                            setModelId(
                              models.find((m) => m.scope === "platform")?.id ??
                                "",
                            );
                        }}
                      >
                        <option value="personal">{t("Only me")}</option>
                        <option value="organization">
                          {t("Organization")}
                        </option>
                      </select>
                    </label>
                  )
                )}
                {user.organizationPermissions.includes("integrations:use") && (
                  <div className="mt-5 rounded-xl border border-stone-200 p-4 dark:border-white/10">
                    <label className="flex items-center gap-2 text-sm font-semibold">
                      <input
                        type="checkbox"
                        checked={eventTriggerEnabled}
                        onChange={(e) =>
                          setEventTriggerEnabled(e.target.checked)
                        }
                      />
                      {t("Trigger from an inbound integration event")}
                    </label>
                    {eventTriggerEnabled && (
                      <div className="mt-3 space-y-3">
                        <label className="block">
                          <span className="label">{t("Integration")}</span>
                          <select
                            className="field"
                            value={eventIntegrationId}
                            onChange={(e) =>
                              setEventIntegrationId(e.target.value)
                            }
                          >
                            <option value="">
                              {t("Choose an integration")}
                            </option>
                            {integrations.map((integration) => (
                              <option
                                key={integration.id}
                                value={integration.id}
                              >
                                {integration.name} · {integration.kind}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="label">{t("Event type")}</span>
                          <select
                            className="field"
                            value={eventType}
                            onChange={(e) =>
                              setEventType(
                                e.target
                                  .value as AutomationEventTrigger["eventType"],
                              )
                            }
                          >
                            <option value="message_received">
                              {t("Message received")}
                            </option>
                            <option value="order_received">
                              {t("Order received")}
                            </option>
                            <option value="reservation_created">
                              {t("Reservation created")}
                            </option>
                            <option value="payment_updated">
                              {t("Payment updated")}
                            </option>
                            <option value="invoice_received">
                              {t("Invoice received")}
                            </option>
                            <option value="review_received">
                              {t("Review received")}
                            </option>
                            <option value="delivery_updated">
                              {t("Delivery updated")}
                            </option>
                            <option value="payroll_updated">
                              {t("Payroll updated")}
                            </option>
                            <option value="procurement_updated">
                              {t("Procurement updated")}
                            </option>
                            <option value="lead_received">
                              {t("Lead received")}
                            </option>
                            <option value="comment_received">
                              {t("Comment received")}
                            </option>
                            <option value="mention_received">
                              {t("Mention received")}
                            </option>
                            <option value="metric_alert">
                              {t("Metric alert")}
                            </option>
                          </select>
                        </label>
                        <div className="grid gap-3 md:grid-cols-3">
                          <label>
                            <span className="label">
                              {t("Sender contains")}
                            </span>
                            <input
                              className="field"
                              value={senderFilter}
                              onChange={(e) => setSenderFilter(e.target.value)}
                            />
                          </label>
                          <label>
                            <span className="label">
                              {t("Subject contains")}
                            </span>
                            <input
                              className="field"
                              value={subjectFilter}
                              onChange={(e) => setSubjectFilter(e.target.value)}
                            />
                          </label>
                          <label>
                            <span className="label">{t("Body contains")}</span>
                            <input
                              className="field"
                              value={bodyFilter}
                              onChange={(e) => setBodyFilter(e.target.value)}
                            />
                          </label>
                        </div>
                        <label className="block">
                          <span className="label">
                            {t("Conversation mapping")}
                          </span>
                          <select
                            className="field"
                            value={conversationMode}
                            onChange={(e) =>
                              setConversationMode(
                                e.target.value as typeof conversationMode,
                              )
                            }
                          >
                            <option value="thread">
                              {t("One conversation per email thread")}
                            </option>
                            <option value="event">
                              {t("New conversation for every message")}
                            </option>
                          </select>
                        </label>
                        <label className="block">
                          <span className="label">
                            {t("External response")}
                          </span>
                          <select
                            className="field"
                            value={responseMode}
                            onChange={(e) =>
                              setResponseMode(
                                e.target.value as typeof responseMode,
                              )
                            }
                          >
                            <option value="none">
                              {t("Keep the result in Lumen")}
                            </option>
                            <option value="full_response">
                              {t("Send the full response by email")}
                            </option>
                            <option value="tool">
                              {t("Let the AI decide with reply_to_source")}
                            </option>
                          </select>
                        </label>
                        {!integrations.length && (
                          <p className="text-xs text-amber-700">
                            {t(
                              "Connect an integration in Settings → Integrations\n                            first.",
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-5 text-xs leading-5 text-stone-400">
                  {t(
                    "Write the prompt and choose its agent, tools, MCP functions, skills, AI parameters, workspace, and repository in the shared message composer. Press Save there when ready.",
                  )}
                </p>
              </div>
            </div>
          </section>
        )}
        <div className="mt-7 grid gap-6 lg:grid-cols-2">
          <section>
            <h2 className="mb-3 font-bold">{t("Schedules")}</h2>
            <div className="space-y-3">
              {items.map((item) => {
                const recent = recentByAutomation.get(item.id);
                return (
                  <article key={item.id} className="panel p-4">
                    <div className="flex gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-lumen-50 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-200">
                        <CalendarClock size={19} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <strong className="truncate">{item.name}</strong>
                          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] dark:bg-white/10">
                            {item.visibility}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-stone-400">
                          {item.nextRunAt
                            ? `Next: ${new Date(item.nextRunAt).toLocaleString()}`
                            : "Paused"}
                          {recent ? ` · Last: ${recent.status}` : ""}
                        </p>
                      </div>
                      {(item.ownerUserId === user.id ||
                        user.organizationPermissions.includes(
                          "automations:manage",
                        )) && (
                        <button
                          className="button p-2"
                          title={t("Edit")}
                          aria-label={t("Edit")}
                          onClick={() => openEdit(item)}
                        >
                          <Pencil size={15} />
                        </button>
                      )}
                      <button
                        className="button p-2"
                        title={
                          item.lifecycle === "published"
                            ? t("Run now")
                            : "Publish this automation before running it"
                        }
                        disabled={item.lifecycle !== "published"}
                        onClick={() =>
                          void api(`/automations/${item.id}/run`, {
                            method: "POST",
                          }).then(load)
                        }
                      >
                        <Play size={15} />
                      </button>
                      <button
                        className="button flex items-center gap-1.5 px-2 py-1.5"
                        title={item.enabled ? t("Pause") : t("Resume")}
                        aria-label={item.enabled ? t("Pause") : t("Resume")}
                        disabled={togglingId === item.id}
                        onClick={() => void toggle(item)}
                      >
                        <Power size={15} />
                        <span className="hidden text-xs sm:inline">
                          {item.enabled ? t("Pause") : t("Resume")}
                        </span>
                      </button>
                      <button
                        className="button p-2 text-red-500"
                        title={t("Delete")}
                        onClick={() =>
                          confirm(`Delete ${item.name}?`) &&
                          void api(`/automations/${item.id}`, {
                            method: "DELETE",
                          }).then(load)
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                );
              })}
              {!items.length && (
                <AutomationEmptyState text={t("No automations yet.")} />
              )}
            </div>
          </section>
          <section>
            <h2 className="mb-3 font-bold">{t("Recent runs")}</h2>
            <div className="space-y-2">
              {runs.slice(0, 30).map((run) => (
                <Link
                  to={`/settings/automations/runs/${run.id}`}
                  key={run.id}
                  className="panel flex items-center gap-3 p-3 transition hover:border-lumen-300"
                >
                  <AutomationRunIcon status={run.status} />
                  <div className="min-w-0 flex-1">
                    <strong className="block truncate text-sm">
                      {run.automationName}
                    </strong>
                    <span className="text-xs text-stone-400">
                      {new Date(run.scheduledFor).toLocaleString()}
                    </span>
                  </div>
                  <span className="text-xs text-stone-500 capitalize">
                    {run.status}
                  </span>
                </Link>
              ))}
              {!runs.length && (
                <AutomationEmptyState text="Runs will appear here." />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
